(() => {
  'use strict';

  if (window.__abmiumCatalogueApp) return;
  window.__abmiumCatalogueApp = true;

  const ROOT_SELECTOR = '[data-abm-catalogue]';
  const PAGE_SIZE = 20;
  const controllers = new WeakMap();
  const cardCache = new Map();
  const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);

  class CatalogueController {
    constructor(root) {
      this.root = root;
      this.apiBase = String(root.dataset.abmApiBase || '').replace(/\/$/, '');
      this.results = root.querySelector('.abm-catalogue__results');
      this.filters = root.querySelector('[data-abm-filters]');
      this.count = root.querySelector('[data-abm-catalogue-count]');
      this.request = null;
      this.sequence = 0;
      this.initialUrl = location.href;
      this.serverResults = Boolean(this.results?.querySelector('[data-abm-server-products]'));
    }

    showUnavailable() {
      this.root.dataset.abmiumApi = 'unavailable';
      if (this.count) this.count.textContent = 'Unavailable';
      if (this.filters) this.filters.innerHTML = '<div class="abm-filters__head abm-sidebar-head"><h2>Refine</h2><button type="button" data-abm-close-filters aria-label="Close">×</button></div><p class="abm-filter__loading">Filters are temporarily unavailable.</p>';
      if (this.results && !(this.serverResults && location.href === this.initialUrl)) this.results.innerHTML = '<div class="abm-catalogue__empty" role="alert"><h2>Catalogue temporarily unavailable</h2><p>Please try again shortly.</p><button type="button" data-abm-retry>Try again</button></div>';
      this.filters?.setAttribute('aria-busy', 'false');
    }

    showInitialLoader() {
      if (this.root.dataset.abmiumApi === 'ready') return;
      this.root.dataset.abmiumApi = 'loading';
      if (this.count) this.count.textContent = 'Loading…';
      this.filters?.setAttribute('aria-busy', 'true');

    }

    setFiltersOpen(open) {
      if (!this.filters) return;
      this.filters.classList.toggle('is-open', open);
      this.root.querySelector('[data-abm-open-filters]')?.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('abm-filter-drawer-open', open);
      if (open) this.filters.querySelector('[data-abm-close-filters]')?.focus({ preventScroll: true });
      else this.root.querySelector('[data-abm-open-filters]')?.focus({ preventScroll: true });
    }

    context() {
      const explicit = this.root.dataset.abmContext;
      if (explicit === 'target') return { context: 'search', q: this.root.dataset.abmSearchTerms || '' };
      const collection = location.pathname.match(/^\/collections\/([^/?#]+)/);
      if (collection) return { context: 'collection', handle: decodeURIComponent(collection[1]) };
      return { context: 'search', q: new URL(location.href).searchParams.get('q') || '*' };
    }

    apiUrl() {
      const url = new URL('/api/search', `${this.apiBase}/`);
      const current = new URL(location.href);
      Object.entries(this.context()).forEach(([key, value]) => url.searchParams.set(key, value));
      current.searchParams.forEach((value, key) => {
        if (key.startsWith('filter.') || key === 'page' || key === 'sort_by') url.searchParams.append(key, value);
      });
      url.searchParams.set('_catalogue', '7');
      url.searchParams.set('per_page', String(PAGE_SIZE));
      url.searchParams.set('facet_mode', 'summary');
      return url;
    }

    facetUrl(key) {
      const url = this.apiUrl();
      url.pathname = '/api/facets';
      url.searchParams.delete('facet_mode');
      url.searchParams.delete('page');
      url.searchParams.delete('per_page');
      url.searchParams.set('facet_group', key);
      return url;
    }

    cardUrl(handles) {
      const url = new URL('/search', location.origin);
      url.searchParams.set('view', 'abmium-cards');
      url.searchParams.set('q', handles.join('|'));
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }

    async cardHtml(handles, signal) {
      const chunks = [];
      for (let index = 0; index < handles.length; index += 20) chunks.push(handles.slice(index, index + 20));
      const html = await Promise.all(chunks.map(async chunk => {
        const url = this.cardUrl(chunk).toString();
        let request = cardCache.get(url);
        if (!request) {
          request = fetch(url, { credentials: 'same-origin', signal, priority: 'high' }).then(response => {
            if (!response.ok) throw new Error(`Card request failed: ${response.status}`);
            return response.text();
          }).catch(error => {
            cardCache.delete(url);
            throw error;
          });
          cardCache.set(url, request);
          if (cardCache.size > 12) cardCache.delete(cardCache.keys().next().value);
        }
        return request;
      }));
      return html.join('');
    }

    selectedUrl(param, value, checked) {
      const url = new URL(location.href);
      const values = url.searchParams.getAll(param).filter(item => item !== value);
      url.searchParams.delete(param);
      values.forEach(item => url.searchParams.append(param, item));
      if (checked) url.searchParams.append(param, value);
      url.searchParams.delete('page');
      return url;
    }

    facetBody(key, facet) {
      if (facet?.type === 'price') {
        return `<div class="abm-filter__body"><form data-abm-price-form><label>Minimum price<input type="number" min="0" step="0.01" name="filter.v.price.gte" value="${facet.selectedMin ?? ''}" placeholder="${Number(facet.min).toFixed(2)}"></label><label>Maximum price<input type="number" min="0" step="0.01" name="filter.v.price.lte" value="${facet.selectedMax ?? ''}" placeholder="${Number(facet.max).toFixed(2)}"></label><button type="submit">Apply</button></form></div>`;
      }
      const options = facet.values.map(value => {
          const rawValue = value.raw?.[0] || value.label;
          const id = `abm-${key}-${btoa(unescape(encodeURIComponent(rawValue))).replace(/[^a-z0-9]/gi, '').slice(0, 18)}`;
          const count = value.count == null ? '' : `<small>${Number(value.count).toLocaleString()}</small>`;
          return `<li><label for="${id}"><input id="${id}" type="checkbox" name="${escapeHtml(facet.param)}" value="${escapeHtml(rawValue)}"${value.selected ? ' checked' : ''}${value.count === 0 && !value.selected ? ' disabled' : ''}><span>${escapeHtml(value.label)}</span>${count}</label></li>`;
      }).join('');
      return `<div class="abm-filter__body"><ul>${options}</ul></div>`;
    }

    async hydrateFacet(details) {
      if (details.dataset.abmFacetRendered === 'true') return;
      const key = details.dataset.abmFacetGroup;
      const mount = details.querySelector('[data-abm-filter-body]');
      if (!key || !mount || details.dataset.abmFacetLoading === 'true') return;
      const sequence = this.sequence;
      details.dataset.abmFacetLoading = 'true';
      mount.innerHTML = '<p class="abm-filter__loading">Loading options…</p>';
      try {
        const response = await fetch(this.facetUrl(key), { headers: { Accept: 'application/json' }, mode: 'cors', credentials: 'omit', signal: this.request?.signal });
        if (!response.ok) throw new Error(`Facet request failed: ${response.status}`);
        const payload = await response.json();
        if (sequence !== this.sequence || !details.isConnected) return;
        const facet = payload.facets?.[key];
        if (!payload.ok || !facet || !details.isConnected) throw new Error(payload.error || 'Facet unavailable');
        this.facetPayload.facets[key] = facet;
        mount.outerHTML = this.facetBody(key, facet);
        details.dataset.abmFacetRendered = 'true';
      } catch (error) {
        if (sequence !== this.sequence || error.name === 'AbortError') return;
        if (details.isConnected) mount.innerHTML = '<p class="abm-filter__loading">Unable to load options. Close and try again.</p>';
        console.error('[ABMIUM facet]', error);
      } finally {
        delete details.dataset.abmFacetLoading;
      }
    }

    renderFilters(payload) {
      if (!this.filters) return;
      this.facetPayload = payload;
      const body = payload.facetOrder.map(key => {
        const facet = payload.facets[key];
        if (!facet) return '';
        const selectedCount = Number(facet.selectedCount || 0);
        const rendered = Boolean(facet.values?.length || (facet.type === 'price' && facet.min !== undefined));
        const content = rendered ? this.facetBody(key, facet) : '<div data-abm-filter-body></div>';
        return `<details class="abm-filter"${selectedCount ? ' open' : ''} data-abm-facet-group="${escapeHtml(key)}" data-abm-facet-rendered="${rendered}"><summary><span>${escapeHtml(facet.label)}</span>${selectedCount ? `<b>${selectedCount}</b>` : ''}</summary>${content}</details>`;
      }).join('');
      this.filters.innerHTML = `<div class="abm-filters__head abm-sidebar-head"><h2>Refine</h2><button type="button" data-abm-close-filters aria-label="Close">×</button></div><div data-abm-cloudflare-filters>${body}</div>`;
      this.filters.setAttribute('aria-busy', 'false');
      this.filters.querySelectorAll('[data-abm-facet-group]').forEach((details, index) => {
        if (details.open || index < 2) this.hydrateFacet(details);
      });
    }

    renderPagination(payload) {
      if (payload.pages <= 1) return '';
      const from = Math.max(1, payload.page - 2);
      const to = Math.min(payload.pages, payload.page + 2);
      const items = [];
      items.push(`<button type="button" data-abm-page="1" aria-label="First page"${payload.page === 1 ? ' disabled' : ''}>First</button>`);
      items.push(`<button type="button" data-abm-page="${Math.max(1, payload.page - 1)}" aria-label="Previous page"${payload.page === 1 ? ' disabled' : ''}>Previous</button>`);
      if (from > 1) items.push('<span class="abm-pagination__ellipsis" aria-hidden="true">…</span>');
      for (let page = from; page <= to; page += 1) items.push(`<button type="button" data-abm-page="${page}"${page === payload.page ? ' aria-current="page"' : ''}>${page}</button>`);
      if (to < payload.pages) items.push('<span class="abm-pagination__ellipsis" aria-hidden="true">…</span>');
      items.push(`<button type="button" data-abm-page="${Math.min(payload.pages, payload.page + 1)}" aria-label="Next page"${payload.page === payload.pages ? ' disabled' : ''}>Next</button>`);
      items.push(`<button type="button" data-abm-page="${payload.pages}" aria-label="Last page"${payload.page === payload.pages ? ' disabled' : ''}>Last</button>`);
      return `<nav class="abm-pagination" aria-label="Catalogue pagination"><p>Page <strong>${Number(payload.page).toLocaleString()}</strong> of <strong>${Number(payload.pages).toLocaleString()}</strong></p><div>${items.join('')}</div></nav>`;
    }

    async load({ push = false } = {}) {
      if (!this.apiBase || !this.results) return;
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const sequence = ++this.sequence;
      this.showInitialLoader();
      this.root.classList.add('is-updating');
      this.root.setAttribute('aria-busy', 'true');
      const timeout = setTimeout(() => request.abort(new DOMException('Catalogue timed out', 'TimeoutError')), 12000);
      const sort = this.root.querySelector('[data-abm-sort]');
      if (sort) sort.value = new URL(location.href).searchParams.get('sort_by') || 'relevance';
      try {
        const response = await fetch(this.apiUrl(), { headers: { Accept: 'application/json' }, mode: 'cors', credentials: 'omit', signal: request.signal });
        if (!response.ok) {
          const error = new Error(`Catalogue request failed: ${response.status}`);
          error.status = response.status;
          throw error;
        }
        const payload = await response.json();
        if (!payload.ok) throw new Error(payload.error || 'Catalogue request failed');
        if (sequence !== this.sequence) return;
        const cardsPromise = this.cardHtml(payload.handles, request.signal);
        this.renderFilters(payload);
        if (this.count) this.count.textContent = Number(payload.total).toLocaleString();
        const cards = await cardsPromise;
        if (sequence !== this.sequence) return;
        this.serverResults = false;
        this.results.innerHTML = cards
          ? `<div class="abm-product-grid abm-grid">${cards}</div>${this.renderPagination(payload)}`
          : '<div class="abm-catalogue__empty"><h2>No matching products</h2><p>Try removing a filter or using a broader search.</p></div>';
        this.root.dataset.abmiumApi = 'ready';
        if (push) history.pushState({ abmiumCatalogue: true }, '', this.apiUrlToPageUrl());
      } catch (error) {
        if (sequence === this.sequence && error.name !== 'AbortError') {
          this.showUnavailable();
          console.error('[ABMIUM catalogue]', error);
        }
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) {
          this.root.classList.remove('is-updating');
          this.root.removeAttribute('aria-busy');
        }
      }
    }

    apiUrlToPageUrl() {
      return location.href;
    }

    updatePage(page) {
      const url = new URL(location.href);
      if (page <= 1) url.searchParams.delete('page');
      else url.searchParams.set('page', String(page));
      history.pushState({ abmiumCatalogue: true }, '', url);
      this.load();
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    bind() {
      this.root.addEventListener('toggle', event => {
        const details = event.target.closest?.('[data-abm-facet-group]');
        if (details?.open) this.hydrateFacet(details);
      }, true);
      this.root.addEventListener('change', event => {
        const checkbox = event.target.closest('[data-abm-cloudflare-filters] input[type="checkbox"]');
        if (checkbox) {
          history.pushState({ abmiumCatalogue: true }, '', this.selectedUrl(checkbox.name, checkbox.value, checkbox.checked));
          this.load();
          return;
        }
        const sort = event.target.closest('[data-abm-sort]');
        if (sort) {
          const url = new URL(location.href);
          if (sort.value) url.searchParams.set('sort_by', sort.value); else url.searchParams.delete('sort_by');
          url.searchParams.delete('page');
          if (this.root.dataset.abmiumApi !== 'ready') {
            location.assign(url);
            return;
          }
          history.pushState({ abmiumCatalogue: true }, '', url);
          this.load();
        }
      });
      this.root.addEventListener('click', event => {
        const retry = event.target.closest('[data-abm-retry]');
        if (retry) {
          event.preventDefault();
          this.load();
          return;
        }
        const page = event.target.closest('[data-abm-page]');
        if (page) {
          event.preventDefault();
          this.updatePage(Number(page.dataset.abmPage));
          return;
        }
        const open = event.target.closest('[data-abm-open-filters]');
        const close = event.target.closest('[data-abm-close-filters]');
        if (open || close) {
          event.preventDefault();
          this.setFiltersOpen(Boolean(open));
          if (open && this.root.dataset.abmiumApi === 'unavailable') this.load();
        }
      });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.filters?.classList.contains('is-open')) this.setFiltersOpen(false);
      });
      this.root.addEventListener('submit', event => {
        const priceForm = event.target.closest('[data-abm-price-form]');
        if (priceForm) {
          event.preventDefault();
          const url = new URL(location.href);
          const data = new FormData(priceForm);
          ['filter.v.price.gte', 'filter.v.price.lte'].forEach(name => {
            const value = String(data.get(name) || '').trim();
            if (value) url.searchParams.set(name, value); else url.searchParams.delete(name);
          });
          url.searchParams.delete('page');
          history.pushState({ abmiumCatalogue: true }, '', url);
          this.load();
          return;
        }
        const form = event.target.closest('[data-abm-native-filters], [data-abm-sort-form]');
        if (form) event.preventDefault();
      });
      this.load();
    }
  }

  function start(root) {
    if (!root || controllers.has(root)) return;
    const controller = new CatalogueController(root);
    controllers.set(root, controller);
    controller.bind();
  }

  function scan() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(start);
  }

  window.addEventListener('popstate', () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
      start(root);
      if (root.dataset.abmiumApi === 'ready') controllers.get(root)?.load();
    });
  });
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', scan, { once: true }) : scan();
})();

